import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useApplyFieldErrors } from '@nucleus-commerce/admin-kit/errors';
import type { Organization } from '@nucleus-commerce/api-client/platform';
import { ApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';
import { ServerError } from '@/shared/ui/server-error';

import { useOrganizationActions, type OrganizationAction } from '../hooks/use-organization-actions';
import { closeSchema, type CloseValues } from '../schemas/profile-schema';
import { reasonSchema, type ReasonValues } from '../schemas/reason-schema';

export interface OrganizationActionDialogProps {
  organization: Organization;
  /** The transition being confirmed; null closes the dialog. */
  action: OrganizationAction | null;
  onClose: () => void;
  /** Called once a delete succeeded; the organization no longer exists, so the caller leaves the page. */
  onDeleted?: () => void;
}

/**
 * Confirmation for every organization transition. Suspend collects the reason the owners are
 * emailed; close takes an optional reason and, once the API reports open orders, a force switch;
 * reactivate, reopen and delete are plain confirms.
 */
export function OrganizationActionDialog({
  organization,
  action,
  onClose,
  onDeleted,
}: OrganizationActionDialogProps) {
  // Keep the last action while the close animation plays so the content does not vanish.
  const [lastAction, setLastAction] = useState(action);
  if (action !== null && action !== lastAction) setLastAction(action);
  const current = action ?? lastAction;

  return (
    <AlertDialog
      open={action !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <AlertDialogContent>
        {current === 'close' ? (
          <CloseForm key={current} organization={organization} onDone={onClose} />
        ) : current ? (
          <ActionForm
            key={current}
            organization={organization}
            action={current}
            onDone={onClose}
            onDeleted={onDeleted}
          />
        ) : null}
      </AlertDialogContent>
    </AlertDialog>
  );
}

interface ActionFormProps {
  organization: Organization;
  action: Exclude<OrganizationAction, 'close'>;
  onDone: () => void;
  onDeleted?: (() => void) | undefined;
}

function ActionForm({ organization, action, onDone, onDeleted }: ActionFormProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const actions = useOrganizationActions(organization.id);
  const needsReason = action === 'suspend';
  const destructive = needsReason || action === 'delete';
  const resolver = useI18nResolver(reasonSchema);
  const form = useForm<ReasonValues>({
    ...(needsReason ? { resolver } : {}),
    defaultValues: { reason: '' },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const isPending = actions[action].isPending;

  const run = (values: ReasonValues): Promise<unknown> => {
    switch (action) {
      case 'suspend':
        return actions.suspend.mutateAsync(values);
      case 'reactivate':
        return actions.reactivate.mutateAsync();
      case 'reopen':
        return actions.reopen.mutateAsync();
      case 'delete':
        return actions.delete.mutateAsync();
    }
  };

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await run(values);
      onDone();
      if (action === 'delete') onDeleted?.();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <AlertDialogHeader>
          <AlertDialogTitle>{t(`organizations:actions.${action}.title`)}</AlertDialogTitle>
          <AlertDialogDescription>
            {t(`organizations:actions.${action}.description`, { name: organization.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {needsReason ? (
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('organizations:fields.reason')}</FormLabel>
                <FormControl>
                  <Textarea
                    autoFocus
                    rows={4}
                    placeholder={t('organizations:fields.reasonHint')}
                    {...field}
                  />
                </FormControl>
                <FormDescription>{t('organizations:actions.suspend.emailed')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>{t('common:actions.cancel')}</AlertDialogCancel>
          <Button
            type="submit"
            variant={destructive ? 'destructive' : 'default'}
            disabled={isPending}
          >
            {t(`organizations:actions.${action}.confirm`)}
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
}

/** The open order count from a 409 OPEN_ORDERS, or null for any other outcome. */
function openOrdersOf(error: unknown): number | null {
  if (!(error instanceof ApiError) || error.code !== 'OPEN_ORDERS') return null;
  const count = error.meta.openOrders;
  return typeof count === 'number' ? count : 0;
}

interface CloseFormProps {
  organization: Organization;
  onDone: () => void;
}

/** Close signs everyone out, stops the API keys and pauses live stores. Open orders need force. */
function CloseForm({ organization, onDone }: CloseFormProps) {
  const { t } = useTranslation(['organizations', 'common']);
  const { close } = useOrganizationActions(organization.id);
  const form = useForm<CloseValues>({
    resolver: useI18nResolver(closeSchema),
    defaultValues: { reason: '', force: false },
  });
  const applyFieldErrors = useApplyFieldErrors(form.setError);
  const openOrders = openOrdersOf(close.error);

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await close.mutateAsync({
        ...(values.reason ? { reason: values.reason } : {}),
        ...(values.force ? { force: true } : {}),
      });
      onDone();
    } catch (error) {
      applyFieldErrors(error);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('organizations:actions.close.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('organizations:actions.close.description', { name: organization.name })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <FormField
          control={form.control}
          name="reason"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('organizations:fields.closeReason')}</FormLabel>
              <FormControl>
                <Textarea
                  autoFocus
                  rows={3}
                  placeholder={t('organizations:fields.closeReasonHint')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {openOrders !== null ? (
          <>
            <Alert variant="destructive" role="alert">
              <AlertTitle>
                {t('organizations:actions.close.openOrders', { count: openOrders })}
              </AlertTitle>
              <AlertDescription>{t('organizations:actions.close.openOrdersHint')}</AlertDescription>
            </Alert>
            <FormField
              control={form.control}
              name="force"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start justify-between gap-4 rounded-lg border p-3">
                  <div className="flex flex-col gap-1">
                    <FormLabel>{t('organizations:fields.force')}</FormLabel>
                    <FormDescription>{t('organizations:fields.forceHint')}</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
          </>
        ) : close.isError && !close.error.hasFieldErrors ? (
          <ServerError error={close.error} />
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={close.isPending}>
            {t('common:actions.cancel')}
          </AlertDialogCancel>
          <Button type="submit" variant="destructive" disabled={close.isPending}>
            {t('organizations:actions.close.confirm')}
          </Button>
        </AlertDialogFooter>
      </form>
    </Form>
  );
}
